package com.isaias.fyattravel.web.components

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit
import kotlin.js.json
import com.isaias.fyattravel.web.config.APPS_SCRIPT_URL
import com.isaias.fyattravel.web.i18n.WebStringKey
import com.isaias.fyattravel.web.i18n.WebStrings
import com.isaias.fyattravel.web.i18n.WebLocaleStore
import com.isaias.fyattravel.web.theme.ThemeColors

private const val FYAT_WHATSAPP = "+256758250625"

/**
 * Booking Enquiry Form
 * Sends emails via a Google Apps Script Web App (see apps-script/Code.gs)
 * and WhatsApp
 */
@Composable
fun BookingEnquiryForm(initialExpanded: Boolean = false) {
    var fullName by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var phone by remember { mutableStateOf("") }
    val locale = WebLocaleStore.locale.value
    fun t(key: WebStringKey) = WebStrings.get(locale, key)
    fun tf(key: WebStringKey, vararg args: String) = WebStrings.format(locale, key, *args)
    val defaultService = t(WebStringKey.FLIGHT_TAB_RETURN)
    var serviceType by remember { mutableStateOf(defaultService) }
    var departure by remember { mutableStateOf("") }
    var destination by remember { mutableStateOf("") }
    var departureDate by remember { mutableStateOf("") }
    var returnDate by remember { mutableStateOf("") }
    var guests by remember { mutableStateOf(t(WebStringKey.GUESTS_PLACEHOLDER)) }
    var additionalDetails by remember { mutableStateOf("") }
    var website by remember { mutableStateOf("") } // honeypot — must stay empty
    var submitted by remember { mutableStateOf(false) }
    var isSending by remember { mutableStateOf(false) }
    var validationError by remember { mutableStateOf<String?>(null) }

    var isModalRendered by remember { mutableStateOf(false) }
    var isModalVisible by remember { mutableStateOf(false) }

    val serviceOptions = listOf(
        t(WebStringKey.FLIGHT_TAB_RETURN),
        t(WebStringKey.FLIGHT_TAB_ONE_WAY),
        t(WebStringKey.HOTEL),
        t(WebStringKey.CAR_RENTAL),
        t(WebStringKey.EXPLORE_VISA),
        t(WebStringKey.TOUR_PACKAGES),
        t(WebStringKey.EXPLORE_TRANSFER)
    )

    fun buildWhatsAppMessage(): String {
        return buildString {
            appendLine(t(WebStringKey.BOOKING_ENQUIRY_TITLE))
            appendLine("==========================================")
            appendLine("${t(WebStringKey.YOUR_NAME)}: $fullName")
            appendLine("${t(WebStringKey.EMAIL_LABEL)}: $email")
            appendLine("${t(WebStringKey.PHONE_NUMBER)}: $phone")
            appendLine("${t(WebStringKey.SERVICE_LABEL)}: $serviceType")
            appendLine("${t(WebStringKey.FLIGHT_FROM)}: $departure")
            appendLine("${t(WebStringKey.FLIGHT_TO)}: $destination")
            appendLine("${t(WebStringKey.FLIGHT_DEPARTURE)}: $departureDate")
            appendLine("${t(WebStringKey.FLIGHT_RETURN)}: $returnDate")
            appendLine("${t(WebStringKey.GUESTS_AND_CABIN_LABEL)}: $guests")
            if (additionalDetails.isNotBlank()) {
                appendLine("${t(WebStringKey.ADDITIONAL_DETAILS_LABEL)}: $additionalDetails")
            }
        }
    }

    fun validate(): Boolean {
        return when {
            fullName.isBlank() -> { validationError = t(WebStringKey.VALIDATION_ENTER_FULL_NAME); false }
            email.isBlank() && phone.isBlank() -> { validationError = t(WebStringKey.VALIDATION_ENTER_EMAIL_OR_PHONE); false }
            departure.isBlank() -> { validationError = t(WebStringKey.VALIDATION_ENTER_DEPARTURE); false }
            destination.isBlank() -> { validationError = t(WebStringKey.VALIDATION_ENTER_DESTINATION); false }
            else -> { validationError = null; true }
        }
    }

    fun sendEmail() {
        if (!validate()) return
        isSending = true
        validationError = null

        val payload = json(
            "from_name" to fullName,
            "from_email" to if (email.isNotBlank()) email else "Not provided",
            "phone" to if (phone.isNotBlank()) phone else "Not provided",
            "service_type" to serviceType,
            "departure" to departure,
            "destination" to destination,
            "departure_date" to if (departureDate.isNotBlank()) departureDate else "Not specified",
            "return_date" to if (returnDate.isNotBlank()) returnDate else "Not specified",
            "guests" to guests,
            "message" to if (additionalDetails.isNotBlank()) additionalDetails else "No additional details",
            "website" to website
        )

        // Content-Type: text/plain (not application/json) so this stays a
        // CORS "simple request" — Apps Script can't answer a preflight.
        window.fetch(
            APPS_SCRIPT_URL,
            RequestInit(
                method = "POST",
                headers = Headers().also { h -> h.append("Content-Type", "text/plain;charset=utf-8") },
                body = kotlin.js.JSON.stringify(payload)
            )
        ).then { resp ->
            resp.text().then { text ->
                try {
                    val obj = kotlin.js.JSON.parse<dynamic>(text)
                    if ((obj.result as String?) == "success") {
                        submitted = true
                    } else {
                        val msg = (obj.message as? String) ?: "Unknown error"
                        validationError = "Failed to send: $msg. Try WhatsApp instead."
                    }
                } catch (t: dynamic) {
                    validationError = "Failed to send. Try WhatsApp instead."
                } finally {
                    isSending = false
                }
            }
        }.catch { _ ->
            validationError = "Failed to send — check your connection. Try WhatsApp instead."
            isSending = false
        }
    }

    fun sendWhatsApp() {
        if (!validate()) return
        val message = buildWhatsAppMessage()
        val encoded = js("encodeURIComponent")(message) as String
        window.open("https://wa.me/$FYAT_WHATSAPP?text=$encoded", "_blank")
        submitted = true
    }

    fun openModal() {
        if (!isModalRendered) {
            isModalRendered = true
            window.setTimeout({ isModalVisible = true }, 12)
        } else {
            isModalVisible = true
        }
    }

    fun closeModal() {
        isModalVisible = false
        window.setTimeout({ isModalRendered = false }, 360)
    }

    DisposableEffect(isModalRendered) {
        val body = document.body
        val previousOverflow = body?.style?.getPropertyValue("overflow")
        if (isModalRendered) {
            body?.style?.setProperty("overflow", "hidden")
        } else {
            body?.style?.setProperty("overflow", previousOverflow ?: "")
        }
        onDispose {
            body?.style?.setProperty("overflow", previousOverflow ?: "")
        }
    }

    Div({
        style {
            backgroundColor(ThemeColors.surface)
            borderRadius(0.75.em)
            property("box-shadow", "0 2px 8px ${ThemeColors.shadow}")
            property("border", "1px solid ${ThemeColors.divider}")
            marginTop(1.5.em)
            marginBottom(1.5.em)
            overflow("hidden")
            property("transition", "transform 360ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 360ms ease")
            property("transform", if (isModalVisible) "translateY(-4px) scale(0.995)" else "translateY(0px) scale(1)")
            property("box-shadow", if (isModalVisible) "0 10px 25px ${ThemeColors.shadow}" else "0 2px 8px ${ThemeColors.shadow}")
        }
        onClick { openModal() }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                padding(0.875.em, 1.25.em)
                cursor("pointer")
                backgroundColor(ThemeColors.surface)
                property("border-bottom", "1px solid ${ThemeColors.divider}")
            }
        }) {
            Div {
                Div({
                    style {
                        fontWeight("700")
                        fontSize(1.em)
                        color(ThemeColors.textPrimary)
                    }
                }) { Text(WebStrings.t(WebStringKey.BOOK_YOUR_JOURNEY)) }
                Div({
                    style {
                        fontSize(0.8.em)
                        color(ThemeColors.textSecondary)
                        marginTop(0.1.em)
                    }
                }) { Text(WebStrings.t(WebStringKey.CLICK_TO_FILL_DETAILS)) }
            }
            Div({
                style {
                    width(26.px)
                    height(26.px)
                    borderRadius(50.percent)
                    backgroundColor(ThemeColors.primaryMuted)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    color(ThemeColors.primary)
                    fontWeight("700")
                    fontSize(0.8.em)
                }
            }) { Text("▼") }
        }

        Div({
            style {
                padding(1.em, 1.25.em)
            }
        }) {
            FormField(WebStrings.t(WebStringKey.YOUR_NAME)) {
                Input(InputType.Text) {
                    style {
                        width(100.percent)
                        padding(0.5.em, 0.6.em)
                        borderRadius(0.375.em)
                        property("border", "1px solid ${ThemeColors.border}")
                        backgroundColor(ThemeColors.surface)
                        fontSize(0.85.em)
                        color(ThemeColors.textSecondary)
                        property("outline", "none")
                    }
                    value(fullName)
                    attr("placeholder", t(WebStringKey.ENTER_YOUR_NAME))
                    onFocusIn { openModal() }
                    onInput {
                        fullName = it.value
                        openModal()
                    }
                }
            }

            Div({
                style {
                    display(DisplayStyle.Grid)
                    gridTemplateColumns("1fr 1fr")
                    gap(0.75.em)
                    marginBottom(0.75.em)
                    property("@media (max-width: 640px)", "{ grid-template-columns: 1fr; }")
                }
            }) {
                FormField(WebStrings.t(WebStringKey.EMAIL_LABEL)) {
                    StyledInput(
                        type = InputType.Email,
                        value = email,
                        placeholder = WebStrings.t(WebStringKey.EMAIL_PLACEHOLDER),
                        onInput = { email = it }
                    )
                }
                FormField(WebStrings.t(WebStringKey.PHONE_NUMBER)) {
                    StyledInput(
                        type = InputType.Tel,
                        value = phone,
                        placeholder = "+256...",
                        onInput = { phone = it }
                    )
                }
            }
        }
    }

    if (isModalRendered) {
        Div({
            style {
                position(Position.Fixed)
                top(0.px)
                left(0.px)
                width(100.vw)
                height(100.vh)
                property("z-index", "10000")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                padding(1.25.em)
                backgroundColor(Color("rgba(15, 23, 42, 0.32)"))
                backgroundColor(ThemeColors.shadow)
                property("backdrop-filter", "blur(10px) saturate(120%)")
                property("-webkit-backdrop-filter", "blur(10px) saturate(120%)")
                property("background-image", "radial-gradient(circle at 20% 15%, rgba(34,197,94,0.20), transparent 45%), radial-gradient(circle at 80% 85%, rgba(16,185,129,0.14), transparent 42%)")
                opacity(if (isModalVisible) 1 else 0)
                property("transition", "opacity 360ms cubic-bezier(0.22, 1, 0.36, 1)")
            }
            onClick { closeModal() }
        }) {
            Div({
                style {
                    width(100.percent)
                    property("max-width", "900px")
                    property("max-height", "90vh")
                    overflow("auto")
                    backgroundColor(ThemeColors.surface)
                    borderRadius(1.em)
                    property("border", "1px solid ${ThemeColors.border}")
                    property("box-shadow", "0 30px 80px ${ThemeColors.shadow}")
                    opacity(if (isModalVisible) 1 else 0)
                    property("transform", if (isModalVisible) "translateY(0px) scale(1)" else "translateY(22px) scale(0.94)")
                    property("transition", "transform 360ms cubic-bezier(0.22, 1, 0.36, 1), opacity 360ms ease")
                }
                onClick { it.stopPropagation() }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        padding(1.em, 1.25.em)
                        property("border-bottom", "1px solid ${ThemeColors.divider}")
                        position(Position.Sticky)
                        top(0.px)
                        backgroundColor(ThemeColors.surface)
                        property("z-index", "2")
                    }
                }) {
                    Div {
                        Div({
                            style {
                                fontWeight("700")
                                color(ThemeColors.textPrimary)
                            }
                        }) { Text(WebStrings.t(WebStringKey.BOOK_YOUR_JOURNEY)) }
                        Div({
                            style {
                                color(ThemeColors.textSecondary)
                                fontSize(0.82.em)
                                marginTop(0.1.em)
                            }
                        }) { Text(WebStrings.t(WebStringKey.CLOSE_FORM)) }
                    }
                    Button({
                        style {
                            backgroundColor(Color.transparent)
                            color(ThemeColors.textSecondary)
                            border(0.px)
                            cursor("pointer")
                            fontWeight("700")
                            fontSize(1.1.em)
                            lineHeight("1")
                        }
                        onClick { closeModal() }
                    }) { Text("✕") }
                }

                Div({ style { padding(1.em, 1.25.em) } }) {
                    HoneypotField(value = website, onInput = { website = it })

                    FormField(WebStrings.t(WebStringKey.YOUR_NAME)) {
                        StyledInput(
                            type = InputType.Text,
                            value = fullName,
                            placeholder = WebStrings.t(WebStringKey.ENTER_YOUR_NAME),
                            onInput = { fullName = it }
                        )
                    }

                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            gridTemplateColumns("1fr 1fr")
                            gap(0.75.em)
                            marginBottom(0.75.em)
                            property("@media (max-width: 640px)", "{ grid-template-columns: 1fr; }")
                        }
                    }) {
                        FormField(WebStrings.t(WebStringKey.EMAIL_LABEL)) {
                            StyledInput(
                                type = InputType.Email,
                                value = email,
                                placeholder = WebStrings.t(WebStringKey.EMAIL_PLACEHOLDER),
                                onInput = { email = it }
                            )
                        }
                        FormField(WebStrings.t(WebStringKey.PHONE_NUMBER)) {
                            StyledInput(
                                type = InputType.Tel,
                                value = phone,
                                placeholder = "+256...",
                                onInput = { phone = it }
                            )
                        }
                    }

                    if (submitted) {
                        Div({
                        style {
                            backgroundColor(ThemeColors.successBg)
                            color(ThemeColors.success)
                            padding(1.em)
                            borderRadius(0.5.em)
                            textAlign("center")
                            fontWeight("600")
                                fontSize(0.95.em)
                            }
                        }) {
                            Text(WebStrings.t(WebStringKey.ENQUIRY_SENT))
                            Div({ style { marginTop(0.75.em) } }) {
                                Button({
                                style {
                                    backgroundColor(Color.transparent)
                                    color(ThemeColors.success)
                                    border(0.px)
                                    cursor("pointer")
                                    fontWeight("600")
                                        textDecoration("underline")
                                        fontSize(0.85.em)
                                    }
                                    onClick { submitted = false }
                                }) { Text(WebStrings.t(WebStringKey.SEND_ANOTHER)) }
                            }
                        }
                    } else {
                        FormField(WebStrings.t(WebStringKey.SERVICE_LABEL)) {
                            Select({
                                style {
                                    width(100.percent)
                                    padding(0.5.em, 0.6.em)
                                    borderRadius(0.375.em)
                                    property("border", "1px solid ${ThemeColors.border}")
                                    backgroundColor(ThemeColors.surface)
                                    fontSize(0.85.em)
                                    color(ThemeColors.textSecondary)
                                    cursor("pointer")
                                    property("outline", "none")
                                }
                                onChange { event ->
                                    val v = event.value
                                    if (v != null) serviceType = v
                                }
                            }) {
                                serviceOptions.forEach { option ->
                                    Option(option, {
                                        if (option == serviceType) attr("selected", "true")
                                    }) { Text(option) }
                                }
                            }
                        }

                        Div({
                            style {
                                display(DisplayStyle.Grid)
                                gridTemplateColumns("1fr 1fr")
                                gap(0.75.em)
                                marginBottom(0.75.em)
                                property("@media (max-width: 640px)", "{ grid-template-columns: 1fr; }")
                            }
                        }) {
                            FormField(WebStrings.t(WebStringKey.FLIGHT_FROM)) {
                                StyledInput(
                                    type = InputType.Text,
                                    value = departure,
                                    placeholder = WebStrings.t(WebStringKey.DEPARTURE_CITY_PLACEHOLDER),
                                    onInput = { departure = it }
                                )
                            }
                            FormField(WebStrings.t(WebStringKey.FLIGHT_TO)) {
                                StyledInput(
                                    type = InputType.Text,
                                    value = destination,
                                    placeholder = WebStrings.t(WebStringKey.DESTINATION_PLACEHOLDER),
                                    onInput = { destination = it }
                                )
                            }
                        }

                        Div({
                            style {
                                display(DisplayStyle.Grid)
                                gridTemplateColumns("1fr 1fr 1fr")
                                gap(0.75.em)
                                marginBottom(0.75.em)
                                property("@media (max-width: 900px)", "{ grid-template-columns: 1fr 1fr; }")
                                property("@media (max-width: 640px)", "{ grid-template-columns: 1fr; }")
                            }
                        }) {
                            FormField(WebStrings.t(WebStringKey.FLIGHT_DEPARTURE)) {
                                Input(InputType.Date) {
                                    style {
                                        width(100.percent)
                                        padding(0.5.em, 0.6.em)
                                        borderRadius(0.375.em)
                                        property("border", "1px solid ${ThemeColors.border}")
                                        backgroundColor(ThemeColors.surface)
                                        fontSize(0.85.em)
                                        color(ThemeColors.textSecondary)
                                        property("outline", "none")
                                    }
                                    value(departureDate)
                                    onInput { departureDate = it.value }
                                }
                            }
                            FormField(WebStrings.t(WebStringKey.FLIGHT_RETURN)) {
                                Input(InputType.Date) {
                                    style {
                                        width(100.percent)
                                        padding(0.5.em, 0.6.em)
                                        borderRadius(0.375.em)
                                        property("border", "1px solid ${ThemeColors.border}")
                                        backgroundColor(ThemeColors.surface)
                                        fontSize(0.85.em)
                                        color(ThemeColors.textSecondary)
                                        property("outline", "none")
                                    }
                                    value(returnDate)
                                    onInput { returnDate = it.value }
                                }
                            }
                            FormField(WebStrings.t(WebStringKey.GUESTS_LABEL)) {
                                StyledInput(
                                    type = InputType.Text,
                                    value = guests,
                                    placeholder = WebStrings.t(WebStringKey.GUESTS_PLACEHOLDER),
                                    onInput = { guests = it }
                                )
                            }
                        }

                        FormField(WebStrings.t(WebStringKey.NOTES_LABEL)) {
                            Div({
                                style {
                                    position(Position.Relative)
                                }
                            }) {
                                TextArea(additionalDetails) {
                                    style {
                                        width(100.percent)
                                        padding(0.5.em, 0.6.em)
                                        borderRadius(0.375.em)
                                        property("border", "1px solid #d1d5db")
                                        backgroundColor(ThemeColors.surface)
                                        fontSize(0.85.em)
                                        color(ThemeColors.textSecondary)
                                        property("outline", "none")
                                        height(50.px)
                                        property("resize", "vertical")
                                    }
                                    attr("placeholder", t(WebStringKey.NOTES_PLACEHOLDER))
                                    onInput { additionalDetails = it.value }
                                }
                            }
                        }

                        validationError?.let { error ->
                            Div({
                        style {
                            backgroundColor(ThemeColors.errorBg)
                            color(ThemeColors.error)
                            padding(0.4.em, 0.6.em)
                            borderRadius(0.375.em)
                            marginBottom(0.75.em)
                                    fontSize(0.8.em)
                                    fontWeight("500")
                                }
                            }) { Text(error) }
                        }

                        Div({
                            style {
                                display(DisplayStyle.Grid)
                                gridTemplateColumns("1fr 1fr")
                                gap(0.6.em)
                                marginTop(0.5.em)
                                property("@media (max-width: 640px)", "{ grid-template-columns: 1fr; }")
                            }
                        }) {
                            Button({
                            style {
                                backgroundColor(if (isSending) ThemeColors.primaryMuted else ThemeColors.primary)
                                color(ThemeColors.textPrimary)
                                padding(0.5.em, 0.75.em)
                                borderRadius(0.375.em)
                                border(0.px)
                                cursor(if (isSending) "not-allowed" else "pointer")
                                fontWeight("600")
                                fontSize(0.85.em)
                                property("box-shadow", if (isSending) "none" else "0 1px 4px ${ThemeColors.shadow}")
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                    gap(0.3.em)
                                    opacity(if (isSending) 0.8 else 1)
                                }
                                onClick { if (!isSending) sendEmail() }
                            }) {
                                if (isSending) {
                                    Span({
                                        ref { el ->
                                            el.innerHTML = """
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" style="transform-origin:center;animation:spin 1s linear infinite;"/>
                                                </svg>
                                            """
                                            onDispose {}
                                        }
                                    })
                                    Text(WebStrings.t(WebStringKey.SENDING))
                                } else {
                                    Text(WebStrings.t(WebStringKey.SEND_EMAIL))
                                }
                            }

                            Button({
                            style {
                                backgroundColor(ThemeColors.whatsapp)
                                color(ThemeColors.textPrimary)
                                padding(0.5.em, 0.75.em)
                                borderRadius(0.375.em)
                                border(0.px)
                                cursor("pointer")
                                fontWeight("600")
                                fontSize(0.85.em)
                                property("box-shadow", "0 1px 4px ${ThemeColors.shadow}")
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                    gap(0.3.em)
                                }
                                onClick { sendWhatsApp() }
                            }) {
                                Text(WebStrings.t(WebStringKey.WHATSAPP))
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FormField(label: String, content: @Composable () -> Unit) {
    Div({
        style { marginBottom(0.5.em) }
    }) {
        Label(null, {
            style {
                display(DisplayStyle.Block)
                fontSize(0.75.em)
                fontWeight("500")
                color(ThemeColors.textSecondary)
                marginBottom(0.2.em)
            }
        }) { Text(label) }
        content()
    }
}

@Composable
private fun StyledInput(
    type: InputType<String>,
    value: String,
    placeholder: String,
    onInput: (String) -> Unit
) {
    Input(type) {
        style {
            width(100.percent)
            padding(0.5.em, 0.6.em)
            borderRadius(0.375.em)
            property("border", "1px solid ${ThemeColors.border}")
            backgroundColor(ThemeColors.surface)
            fontSize(0.85.em)
            color(ThemeColors.textSecondary)
            property("outline", "none")
        }
        value(value)
        attr("placeholder", placeholder)
        onInput { onInput(it.value) }
    }
}

/**
 * Spam honeypot: genuinely invisible to sighted and keyboard users (off-screen,
 * not display:none — screen-reading bots that skip display:none often still
 * see off-screen text; tabindex -1 so a real user tabbing through the form
 * never lands on it). Bots that fill in every field will fill this one too;
 * doPost() in apps-script/Code.gs silently drops the submission when it's non-empty.
 */
@Composable
private fun HoneypotField(value: String, onInput: (String) -> Unit) {
    Div({
        style {
            position(Position.Absolute)
            left((-9999).px)
            width(1.px)
            height(1.px)
            overflow("hidden")
        }
    }) {
        Label(attrs = { attr("for", "website-be") }) { Text("Website") }
        Input(InputType.Text) {
            attr("id", "website-be")
            attr("name", "website")
            attr("autocomplete", "off")
            attr("tabindex", "-1")
            value(value)
            onInput { onInput(it.value) }
        }
    }
}
